// src/modules/financials/components/ProfitLossSection.jsx
import React from 'react';
import { Card, Row, Col, Statistic, Typography, Empty, Divider } from 'antd';
import { Column } from '@ant-design/charts';
import { BarChartOutlined, ArrowUpOutlined, ArrowDownOutlined, MinusCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const sourceColors = { 'Walk-In': '#52c41a', Pledge: '#1890ff', Smelter: '#faad14' };

const ProfitLossSection = ({ data = {}, loading = false }) => {
  const {
    grossProfit = 0, totalProfit = 0, walkInProfit = 0, pledgeProfit = 0,
    totalOperatingExpenses = 0, netProfit = 0, profitBreakdown = [],
    agentCommissions = {},
  } = data;

  const totalAgentCommissions = agentCommissions.total || 0;
  const paidAgentCommissions = agentCommissions.paid || 0;
  const pendingAgentCommissions = agentCommissions.pending || 0;

  // Use grossProfit from API if available, otherwise fall back to totalProfit
  const displayGrossProfit = grossProfit || totalProfit;

  // Filter out smelter profit from breakdown
  const barData = profitBreakdown.filter(p => p.profit !== 0 && p.source !== 'Smelter');

  const barConfig = {
    data: barData,
    xField: 'source',
    yField: 'profit',
    style: {
      radiusTopLeft: 4,
      radiusTopRight: 4,
      fill: (d) => sourceColors[d.source] || '#8c8c8c',
    },
    label: false,
    tooltip: {
      items: [
        (d) => ({
          name: d.source,
          value: inr(d.profit),
        }),
      ],
    },
    axis: {
      y: { labelFormatter: (v) => inr(v) },
    },
  };

  return (
    <Card
      title={<span><BarChartOutlined style={{ color: '#52c41a', marginRight: 8 }} />Profit & Loss</span>}
      style={{ borderRadius: 12, border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      loading={loading}
    >
      {/* Profit Breakdown by Source */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Statistic
            title="Walk-In Profit"
            value={inr(walkInProfit)}
            valueStyle={{ color: walkInProfit >= 0 ? '#3f8600' : '#cf1322', fontSize: 18 }}
            prefix={walkInProfit >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          />
        </Col>
        <Col xs={24} md={12}>
          <Statistic
            title="Pledge Profit"
            value={inr(pledgeProfit)}
            valueStyle={{ color: pledgeProfit >= 0 ? '#3f8600' : '#cf1322', fontSize: 18 }}
            prefix={pledgeProfit >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          />
        </Col>
      </Row>

      {/* Three-tier P/L Summary */}
      <Divider style={{ margin: '16px 0' }} />
      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Statistic
            title="Gross Profit"
            value={inr(displayGrossProfit)}
            valueStyle={{ color: displayGrossProfit >= 0 ? '#3f8600' : '#cf1322', fontSize: 20, fontWeight: 600 }}
            prefix={displayGrossProfit >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          />
        </Col>
        <Col xs={24} md={6}>
          <Statistic
            title="Less: Operating Expenses"
            value={inr(totalOperatingExpenses)}
            valueStyle={{ color: '#cf1322', fontSize: 20, fontWeight: 600 }}
            prefix={<MinusCircleOutlined />}
          />
        </Col>
        <Col xs={24} md={6}>
          <Statistic
            title="Less: Agent Commissions"
            value={inr(totalAgentCommissions)}
            valueStyle={{ color: totalAgentCommissions > 0 ? '#cf1322' : '#8c8c8c', fontSize: 20, fontWeight: 600 }}
            prefix={<MinusCircleOutlined />}
          />
          {totalAgentCommissions > 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Paid: {inr(paidAgentCommissions)} | Pending: {inr(pendingAgentCommissions)}
            </Text>
          )}
        </Col>
        <Col xs={24} md={6}>
          <Card 
            size="small" 
            style={{ 
              background: netProfit >= 0 ? '#f6ffed' : '#fff2f0', 
              borderColor: netProfit >= 0 ? '#b7eb8f' : '#ffccc7' 
            }}
          >
            <Statistic
              title={<Text strong>Net Profit</Text>}
              value={inr(netProfit)}
              valueStyle={{ color: netProfit >= 0 ? '#3f8600' : '#cf1322', fontSize: 22, fontWeight: 700 }}
              prefix={netProfit >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: 24 }}>
        {barData.length > 0 ? (
          <Column {...barConfig} height={280} />
        ) : (
          <Empty description="No profit data for the selected period" />
        )}
      </div>
    </Card>
  );
};

export default ProfitLossSection;
